import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {useEffect, useRef, useState, type VideoHTMLAttributes} from 'react'
import {
  CopilotIcon,
  PeopleIcon,
  BookIcon,
  DeviceMobileIcon,
  IterationsIcon,
  ColumnsIcon,
  PlayIcon,
} from '@primer/octicons-react'
import {
  ThemeProvider,
  Hero,
  Grid,
  Image,
  LogoSuite,
  AnchorNav,
  SectionIntro,
  Bento,
  Link,
  Box,
  Text,
  River,
  Heading,
  Label,
  RiverBreakout,
  Timeline,
  Button,
  Stack,
  Card,
  AnimationProvider,
  OrderedList,
} from '@primer/react-brand'
import {ContentfulFaqGroup} from '../components/contentful/ContentfulFaqGroup/ContentfulFaqGroup'
import {toPayload} from '../lib/types/payload'
import {isFeatureCopilotPage, toContainerPage, toEntryCollection} from '../lib/types/contentful'
import resolveResponse from 'contentful-resolve-response'
import {ExperimentalVideo} from './copilot/ExperimentalVideo'
import {PricingGrouped} from './copilot/PricingGrouped'
import {PricingTable} from './copilot/PricingTable'
import {PricingCards} from './copilot/PricingCards'

export function CopilotIndex() {
  const {copilotSignupPath} = useRoutePayload<{copilotSignupPath: string}>()
  const {copilotForBusinessSignupPath} = useRoutePayload<{copilotForBusinessSignupPath: string}>()
  const {copilotContactSalesPath} = useRoutePayload<{copilotContactSalesPath: string}>()
  const {inShowVideoTreatment} = useRoutePayload<{inShowVideoTreatment: boolean}>()
  const {shouldShowPricingTable} = useRoutePayload<{shouldShowPricingTable: boolean}>()
  const {copilotHydroTracking} = useRoutePayload<{copilotHydroTracking: string}>()
  const {copilotConvoHydroTracking} = useRoutePayload<{copilotConvoHydroTracking: string}>()
  const {copilotSuggestionsHydroTracking} = useRoutePayload<{copilotSuggestionsHydroTracking: string}>()
  const {copilotDocsHydroTracking} = useRoutePayload<{copilotDocsHydroTracking: string}>()
  const {copilotPullRequestsHydroTracking} = useRoutePayload<{copilotPullRequestsHydroTracking: string}>()
  const {contentfulRawJsonResponse} = toPayload(useRoutePayload<unknown>())

  const page = toContainerPage(toEntryCollection(resolveResponse(contentfulRawJsonResponse)).at(0))

  // Hero Video player
  //
  // Video player with custom controls for the Hero

  const {heroVideoLg} = useRoutePayload<{heroVideoLg: string}>()
  const {heroVideoLgPoster} = useRoutePayload<{heroVideoLgPoster: string}>()
  const {heroVideoSm} = useRoutePayload<{heroVideoSm: string}>()

  const videoLgRef = useRef<HTMLVideoElement>(null)
  const videoSmRef = useRef<HTMLVideoElement>(null)
  const [videoState, setVideoState] = useState('playing')
  const [videoButtonLabel, setVideoButtonLabel] = useState('Pause')
  const [VideoIcon, setVideoIcon] = useState(() => ColumnsIcon)

  const handleVideoStateChange = () => {
    if (videoState === 'playing') {
      setVideoState('paused')
      setVideoButtonLabel('Play')
      setVideoIcon(() => PlayIcon)
      if (videoLgRef.current && videoSmRef.current) {
        videoLgRef.current.pause()
        videoSmRef.current.pause()
      }
    } else if (videoState === 'paused') {
      setVideoState('playing')
      setVideoButtonLabel('Pause')
      setVideoIcon(() => ColumnsIcon)
      if (videoLgRef.current && videoSmRef.current) {
        videoLgRef.current.play()
        videoSmRef.current.play()
      }
    } else if (videoState === 'ended') {
      setVideoState('playing')
      setVideoButtonLabel('Pause')
      setVideoIcon(() => ColumnsIcon)
      if (videoLgRef.current && videoSmRef.current) {
        videoLgRef.current.currentTime = 0
        videoSmRef.current.currentTime = 0
        videoLgRef.current.play()
        videoSmRef.current.play()
      }
    }
  }

  const handleVideoStateEnd = () => {
    setVideoState('ended')
    setVideoButtonLabel('Replay')
    setVideoIcon(() => IterationsIcon)
  }

  // Auto-play videos
  //
  // Starts playing videos when they're in view

  interface VideoProps extends VideoHTMLAttributes<HTMLVideoElement> {
    src: string
    poster: string
  }

  function AutoPlayVideo({src, poster, ...props}: VideoProps) {
    const videoRef = useAutoplayVideo()

    return (
      <video ref={videoRef} playsInline={true} muted={true} preload="none" poster={poster} {...props}>
        <source src={src} type="video/mp4; codecs=avc1.4d002a" />
      </video>
    )
  }

  function useAutoplayVideo() {
    const videoRef = useRef<HTMLVideoElement | null>(null)

    useEffect(() => {
      const currentVideoRef = videoRef.current

      const observer = new IntersectionObserver(
        entries => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              if (currentVideoRef) currentVideoRef.play()
            } else {
              if (currentVideoRef) currentVideoRef.pause()
            }
          }
        },
        {
          threshold: 0.5, // Play once 50% of the height is...
          rootMargin: `-25% 0% -25% 0%`, // ...in the center 50% of the viewport
        },
      )

      if (currentVideoRef) {
        observer.observe(currentVideoRef)
      }

      return () => {
        if (currentVideoRef) {
          observer.unobserve(currentVideoRef)
        }
      }
    }, [])

    return videoRef
  }

  // Pricing deep link
  //
  // This is an alternative to using `href="#pricing"` on the Hero "Compare plans" button
  // It seems deep linking currently doesn't work reliably, so we're using `scrollTo()` instead

  const ctaPricingRef = useRef<HTMLButtonElement>(null)
  const sectionPricingRef = useRef<HTMLElement>(null)

  const handlePricingClick = (event: Event) => {
    event.preventDefault()

    if (sectionPricingRef.current) {
      // Set focus to the pricing section
      sectionPricingRef.current.setAttribute('tabindex', '-1')
      sectionPricingRef.current.focus({preventScroll: true})

      // Then scroll to the pricing section
      window.scrollTo({
        top: sectionPricingRef.current.offsetTop,
      })
    }
  }

  useEffect(() => {
    const currentCtaPricingRef = ctaPricingRef.current

    if (currentCtaPricingRef) {
      currentCtaPricingRef.addEventListener('click', handlePricingClick)
    }

    return () => {
      if (currentCtaPricingRef) {
        currentCtaPricingRef.removeEventListener('click', handlePricingClick)
      }
    }
  }, [])

  return (
    <>
      <ThemeProvider colorMode="dark" className="lp-Copilot">
        <section id="hero" className="lp-Section lp-Section--hero">
          <Grid>
            <Grid.Column span={12}>
              <Hero data-hpc align="center" className="lp-Hero">
                <Hero.Label
                  size="large"
                  leadingVisual={<CopilotIcon />}
                  color="purple-red"
                  className="label-purple-green mb-4"
                >
                  Copilot Enterprise now available
                </Hero.Label>
                <Hero.Heading size="1" weight="bold" className="lp-Hero-heading">
                  The world’s most widely adopted AI developer tool.
                </Hero.Heading>
                <div className="lp-Hero-ctaButtons">
                  <Hero.PrimaryAction href="/features/copilot/plans" className="Button--heroCta">
                    Get started with Copilot
                  </Hero.PrimaryAction>
                </div>
              </Hero>

              <Box className="lp-Hero-visual">
                <Box className="lp-Hero-videoContainer">
                  <video
                    autoPlay={true}
                    playsInline={true}
                    muted={true}
                    className="lp-Hero-video lp-Hero-video--landscape hide-reduced-motion"
                    width="1248"
                    height="735"
                    poster={heroVideoLgPoster}
                    ref={videoLgRef}
                    onEnded={() => handleVideoStateEnd()}
                  >
                    <source src={heroVideoLg} type="video/mp4; codecs=avc1.4d002a" />
                  </video>

                  <video
                    autoPlay={true}
                    playsInline={true}
                    muted={true}
                    className="lp-Hero-video lp-Hero-video--portrait hide-reduced-motion"
                    width="539.5"
                    height="682"
                    ref={videoSmRef}
                    onEnded={() => handleVideoStateEnd()}
                  >
                    <source src={heroVideoSm} type="video/mp4; codecs=avc1.4d002a" />
                  </video>

                  <Image
                    className="lp-Hero-videoImage hide-no-pref-motion"
                    src="/images/modules/site/copilot/hero.jpg"
                    alt="Editor with GitHub Copilot Chat"
                    width="1248"
                    loading="lazy"
                  />
                </Box>

                <Box paddingBlockStart={12} className="lp-Hero-videoPlayer">
                  <Button
                    leadingVisual={VideoIcon}
                    variant="subtle"
                    hasArrow={false}
                    className="lp-Hero-videoPlayerButton"
                    onClick={handleVideoStateChange}
                  >
                    {videoButtonLabel}
                  </Button>
                </Box>

                <Box className="lp-Hero-head hide-reduced-motion">
                  <div className="lp-Hero-headSize">
                    <div className="lp-Hero-headBlink js-copilot-head-wrapper">
                      <canvas className="js-copilot-head" data-analytics-event={copilotHydroTracking} />
                    </div>
                  </div>
                </Box>
              </Box>

              <LogoSuite hasDivider={false}>
                <LogoSuite.Heading visuallyHidden>GitHub Copilot is used by</LogoSuite.Heading>
                <LogoSuite.Logobar marquee marqueeSpeed="slow">
                  <Image src="/images/modules/site/copilot/logos/coca-cola.svg" alt="Coca Cola's logo" height={40} />
                  <Image
                    src="/images/modules/site/copilot/logos/coyote-logistics.svg"
                    alt="Coyote Logistics's logo"
                    height={40}
                  />
                  <Image src="/images/modules/site/copilot/logos/duolingo.svg" alt="Duolingo's logo" height={40} />
                  <Image src="/images/modules/site/copilot/logos/stripe.svg" alt="Stripe's logo" height={44} />
                  <Image src="/images/modules/site/copilot/logos/shopify.svg" alt="Shopify's logo" height={40} />
                  <Image
                    src="/images/modules/site/copilot/logos/mercado-libre.svg"
                    alt="Mercado Libre's logo"
                    height={40}
                  />
                  <Image src="/images/modules/site/copilot/logos/mercedes.svg" alt="Mercedes Benz's logo" height={48} />
                  <Image src="/images/modules/site/copilot/logos/fidelity.svg" alt="Fidelity's logo" height={36} />
                  <Image src="/images/modules/site/copilot/logos/dell.svg" alt="Dell's logo" height={60} />
                  <Image src="/images/modules/site/copilot/logos/lemonade.svg" alt="Lemonade's logo" height={36} />
                  <Image
                    src="/images/modules/site/copilot/logos/sas.svg"
                    alt="Scandinavian Airlines's logo"
                    height={36}
                  />
                  <Image src="/images/modules/site/copilot/logos/itau.svg" alt="Itau's logo" height={50} />
                  <Image src="/images/modules/site/copilot/logos/hover.svg" alt="Hover's logo" height={36} />
                  <Image src="/images/modules/site/copilot/logos/lyft.svg" alt="Lyft's logo" height={64} />
                </LogoSuite.Logobar>
              </LogoSuite>
            </Grid.Column>
          </Grid>

          <Box style={{height: 0}}>
            <AnchorNav hideUntilSticky>
              <AnchorNav.Link href="#enterprise">Enterprise-ready</AnchorNav.Link>
              <AnchorNav.Link href="#features">Features</AnchorNav.Link>
              <AnchorNav.Link href="#pricing">Pricing</AnchorNav.Link>
              <AnchorNav.Link href="#faq">FAQs</AnchorNav.Link>
              <AnchorNav.Action href="/features/copilot/plans">Get started</AnchorNav.Action>
            </AnchorNav>
          </Box>
          <div className="lp-Hero-cover" />
        </section>

        <section id="enterprise" className="lp-Section lp-Section--level-1">
          <Grid className="lp-Section-container--centerUntilMedium lp-Grid--noRowGap">
            <Grid.Column span={12}>
              <SectionIntro fullWidth className="lp-SectionIntro">
                <SectionIntro.Label size="large" className="lp-Label--section">
                  Enterprise-ready
                </SectionIntro.Label>
                <SectionIntro.Heading size="1" weight="bold">
                  The competitive advantage developers ask for by name.
                </SectionIntro.Heading>
              </SectionIntro>
            </Grid.Column>

            <Grid.Column span={12}>
              <Bento className="Bento Bento--inset">
                {inShowVideoTreatment ? <ExperimentalVideo /> : null}
                <Bento.Item
                  columnSpan={{xsmall: 12, medium: 6, large: 7}}
                  rowSpan={{xsmall: 4, small: 3, medium: 4, large: 5, xlarge: 5}}
                  visualAsBackground
                >
                  <Bento.Content padding={{xsmall: 'normal', xlarge: 'spacious'}}>
                    <Bento.Heading as="h3" size="3" weight="semibold">
                      Proven to increase developer productivity and accelerate the pace of software development.
                    </Bento.Heading>
                    <Link
                      href="https://github.blog/2022-09-07-research-quantifying-github-copilots-impact-on-developer-productivity-and-happiness/"
                      size="large"
                      variant="default"
                    >
                      Read the research
                    </Link>
                  </Bento.Content>
                  <Bento.Visual>
                    <Image
                      src="/images/modules/site/copilot/enterprise-1.jpg"
                      alt=""
                      width="724"
                      height="540"
                      loading="lazy"
                    />
                  </Bento.Visual>
                </Bento.Item>

                <Bento.Item
                  columnSpan={{xsmall: 12, medium: 6, large: 5}}
                  rowSpan={{xsmall: 3, small: 3, medium: 4, large: 5, xlarge: 5}}
                  visualAsBackground
                >
                  <Bento.Content
                    padding={{xsmall: 'normal', xlarge: 'spacious'}}
                    verticalAlign="end"
                    className="lp-Bento-lastChildNoMarginBottom"
                  >
                    <Bento.Heading as="h3" size="display" className="is-sansSerifAlt">
                      55%
                    </Bento.Heading>
                    <Text as="p" size="400" weight="medium" variant="muted" style={{marginBottom: '0'}}>
                      Faster coding
                    </Text>
                  </Bento.Content>
                  <Bento.Visual position="0% 0%">
                    <Image
                      src="/images/modules/site/copilot/enterprise-2.webp"
                      alt=""
                      width="492"
                      height="540"
                      loading="lazy"
                    />
                  </Bento.Visual>
                </Bento.Item>

                <Bento.Item
                  columnSpan={{xsmall: 12, medium: 6, large: 5}}
                  rowSpan={{xsmall: 3, small: 3, medium: 4, large: 4, xlarge: 5}}
                  visualAsBackground
                >
                  <Bento.Content
                    padding={{xsmall: 'normal', xlarge: 'spacious'}}
                    verticalAlign="end"
                    className="lp-Bento-lastChildNoMarginBottom"
                  >
                    <Bento.Heading as="h3" size="4" weight="semibold">
                      Designed by leaders in AI so you can build with confidence.
                    </Bento.Heading>
                  </Bento.Content>
                  <Bento.Visual position="0% 0%">
                    <Image
                      src="/images/modules/site/copilot/enterprise-3.webp"
                      alt=""
                      width="492"
                      height="540"
                      loading="lazy"
                    />
                  </Bento.Visual>
                </Bento.Item>

                <Bento.Item
                  columnSpan={{xsmall: 12, medium: 6, large: 7}}
                  rowSpan={{xsmall: 3, small: 3, medium: 4, large: 4, xlarge: 5}}
                  visualAsBackground
                >
                  <Bento.Content padding={{xsmall: 'normal', xlarge: 'spacious'}}>
                    <Bento.Heading as="h3" size="3" weight="semibold">
                      Committed to your privacy, security, and trust.
                    </Bento.Heading>
                    <Link href="https://resources.github.com/copilot-trust-center/" size="large" variant="default">
                      Visit the GitHub Copilot Trust Center
                    </Link>
                  </Bento.Content>
                  <Bento.Visual>
                    <Image
                      src="/images/modules/site/copilot/enterprise-4.jpg"
                      alt=""
                      width="724"
                      height="540"
                      loading="lazy"
                    />
                  </Bento.Visual>
                </Bento.Item>

                <Bento.Item
                  columnSpan={12}
                  rowSpan={{xsmall: 5, small: 5, medium: 4, large: 4, xlarge: 5}}
                  flow={{xsmall: 'row', medium: 'column'}}
                  className="Bento-item"
                >
                  <Bento.Content padding={{xsmall: 'normal', xlarge: 'spacious'}}>
                    <Bento.Heading as="h3" size="4" weight="semibold">
                      Duolingo empowers its engineers to be force multipliers for expertise with GitHub Copilot,
                      Codespaces.
                    </Bento.Heading>
                    <Link href="https://github.com/customer-stories/duolingo" size="large" variant="default">
                      Read customer story
                    </Link>
                  </Bento.Content>
                  <Bento.Visual padding="normal">
                    <Image
                      src="/images/modules/site/copilot/enterprise-5.jpg"
                      alt="Mobile device using Duolingo app"
                      width="570"
                      height="532"
                      loading="lazy"
                    />
                  </Bento.Visual>
                </Bento.Item>
              </Bento>
            </Grid.Column>
          </Grid>
        </section>

        <section
          id="productivity"
          className="lp-Section lp-Section--compact lp-Section--level-gradient lp-Section--illu"
        >
          <Grid
            className="lp-Section-container--centerUntilMedium lp-Grid--noRowGap lp-Section--illu-content"
            style={{paddingBottom: '32px'}}
          >
            <Grid.Column span={12}>
              <SectionIntro fullWidth className="lp-SectionIntro">
                <SectionIntro.Heading size="3">
                  The industry <br className="break-whenNarrow" /> standard.
                </SectionIntro.Heading>
              </SectionIntro>
            </Grid.Column>

            <Grid.Column span={12}>
              <AnimationProvider runOnce={true}>
                <Stack
                  direction={{narrow: 'vertical', regular: 'horizontal'}}
                  gap={32}
                  padding="none"
                  className="lp-Productivity"
                >
                  <Box
                    padding={32}
                    className="lp-Productivity-item has-BlurredBg has-GradientBorder"
                    animate="slide-in-up"
                  >
                    <Heading
                      as="h3"
                      weight="medium"
                      className="is-sansSerifAlt"
                      style={{fontSize: '56px', letterSpacing: '-1px'}}
                    >
                      50,000+
                    </Heading>
                    <Text as="p" weight="medium" variant="muted" style={{fontSize: '24px', letterSpacing: '-0.48px'}}>
                      Businesses have adopted GitHub Copilot
                    </Text>
                  </Box>

                  <Box
                    padding={32}
                    className="lp-Productivity-item has-BlurredBg has-GradientBorder"
                    animate="slide-in-up"
                  >
                    <Heading
                      as="h3"
                      weight="medium"
                      className="is-sansSerifAlt"
                      style={{fontSize: '56px', letterSpacing: '-1px'}}
                    >
                      1 in 3
                    </Heading>
                    <Text as="p" weight="medium" variant="muted" style={{fontSize: '24px', letterSpacing: '-0.48px'}}>
                      Fortune 500 companies use GitHub Copilot
                    </Text>
                    <Text size="200" variant="muted" style={{marginTop: '28px'}}>
                      Available for business since Dec 2022
                    </Text>
                  </Box>

                  <Box
                    padding={32}
                    className="lp-Productivity-item has-BlurredBg has-GradientBorder"
                    animate="slide-in-up"
                  >
                    <Heading
                      as="h3"
                      weight="medium"
                      className="is-sansSerifAlt"
                      style={{fontSize: '56px', letterSpacing: '-1px'}}
                    >
                      55%
                    </Heading>
                    <Text as="p" weight="medium" variant="muted" style={{fontSize: '24px', letterSpacing: '-0.48px'}}>
                      Developer preference for GitHub Copilot
                    </Text>
                    <Text size="200" variant="muted" style={{marginTop: '28px'}}>
                      Stack Overflow 2023 Survey
                    </Text>
                  </Box>
                </Stack>
              </AnimationProvider>
            </Grid.Column>
          </Grid>

          <Image
            src="/images/modules/site/copilot/productivity-bg-2.webp"
            alt=""
            width="600"
            height="350"
            className="lp-Productivity-bg--kite2"
            loading="lazy"
          />
          <Image
            src="/images/modules/site/copilot/productivity-bg-1.webp"
            alt=""
            width="706"
            height="338"
            className="lp-Productivity-bg--kite1"
            loading="lazy"
          />
          <Image
            src="/images/modules/site/copilot/productivity-bg-head.png"
            alt=""
            width="234"
            height="204"
            className="lp-Section--illu-img lp-Productivity-bg--mascot"
            loading="lazy"
          />
        </section>

        <section id="features" className="lp-Section lp-Section--noBottomPadding">
          <Grid className="lp-Section-container--centerUntilMedium lp-Grid--noRowGap">
            <Grid.Column span={12}>
              <SectionIntro fullWidth className="lp-SectionIntro">
                <SectionIntro.Label size="large" className="lp-Label--section">
                  Features
                </SectionIntro.Label>
                <SectionIntro.Heading as="h2" size="1" weight="bold">
                  The AI coding assistant <br className="break-whenWide" /> elevating developer workflows.
                </SectionIntro.Heading>
              </SectionIntro>
            </Grid.Column>

            <Grid.Column span={12}>
              <RiverBreakout style={{paddingTop: '0'}}>
                <a
                  href="https://docs.github.com/en/copilot/github-copilot-chat/about-github-copilot-chat#use-cases-for-github-copilot-chat"
                  data-analytics-event={copilotConvoHydroTracking}
                >
                  <RiverBreakout.A11yHeading>Accelerate workflows</RiverBreakout.A11yHeading>
                  <RiverBreakout.Visual aria-hidden="true">
                    <AutoPlayVideo
                      src="/images/modules/site/copilot/features-breakout.mp4"
                      poster="/images/modules/site/copilot/features-breakout-poster.webp"
                      width="1248"
                      height="647"
                      className="d-none d-md-block hide-reduced-motion"
                    />
                    <AutoPlayVideo
                      src="/images/modules/site/copilot/features-breakout-sm.mp4"
                      poster="/images/modules/site/copilot/features-breakout-poster-sm.webp"
                      width="350"
                      height="380"
                      className="d-block d-md-none hide-reduced-motion"
                    />
                    <Image
                      src="/images/modules/site/copilot/features-breakout.webp"
                      alt="Screenshot showing GitHub Copilot"
                      width="1248"
                      height="647"
                      style={{display: 'block'}}
                      className="hide-no-pref-motion"
                      loading="lazy"
                    />
                  </RiverBreakout.Visual>
                </a>
                <RiverBreakout.Content
                  trailingComponent={() => (
                    <Timeline className="lp-Timeline">
                      <Timeline.Item>
                        <em>Improve code quality and security.</em> Developers feel{' '}
                        <a
                          className="lp-Link--inline"
                          href="https://github.blog/2023-10-10-research-quantifying-github-copilots-impact-on-code-quality/"
                        >
                          more confident in their code quality
                        </a>{' '}
                        when authoring code with GitHub Copilot. And with the built-in{' '}
                        <a
                          className="lp-Link--inline"
                          href="https://github.blog/2023-02-14-github-copilot-now-has-a-better-ai-model-and-new-capabilities/#filtering-out-security-vulnerabilities-with-a-new-ai-system"
                        >
                          vulnerability prevention system
                        </a>
                        , insecure coding patterns get blocked in real time.
                      </Timeline.Item>
                      <Timeline.Item>
                        <em>Enable greater collaboration.</em> GitHub Copilot’s the newest member of your team. You can
                        ask general programming questions or very specific ones about your codebase to get answers fast,
                        learn your way around, explain a mysterious regex, or get suggestions on how to improve legacy
                        code.
                      </Timeline.Item>
                    </Timeline>
                  )}
                >
                  <Text>
                    <em>Start a conversation about your codebase.</em> Whether you’re hunting down a bug or designing a
                    new feature—when you’re stuck, ask <br className="break-whenWide" />
                    GitHub Copilot.
                  </Text>
                </RiverBreakout.Content>
              </RiverBreakout>

              <River imageTextRatio="60:40">
                <River.Visual aria-hidden="true">
                  <a
                    href="https://docs.github.com/en/copilot/quickstart#introduction"
                    data-analytics-event={copilotSuggestionsHydroTracking}
                    tabIndex={-1}
                    aria-hidden="true"
                  >
                    <AutoPlayVideo
                      src="/images/modules/site/copilot/features-river-1.mp4"
                      poster="/images/modules/site/copilot/features-river-1-poster.webp"
                      width="708"
                      height="472"
                      className="d-none d-md-block hide-reduced-motion"
                    />
                    <AutoPlayVideo
                      src="/images/modules/site/copilot/features-river-1-sm.mp4"
                      poster="/images/modules/site/copilot/features-river-1-poster-sm.webp"
                      width="350"
                      height="290"
                      className="d-block d-md-none hide-reduced-motion"
                    />
                    <Image
                      src="/images/modules/site/copilot/features-river-1.webp"
                      alt="Editor with GitHub Copilot Chat"
                      width="708"
                      height="472"
                      className="hide-no-pref-motion"
                      loading="lazy"
                    />
                  </a>
                </River.Visual>
                <River.Content>
                  <Heading as="h3">Get AI-based suggestions in real time.</Heading>
                  <Text as="p" variant="muted" className="lp-River-text">
                    GitHub Copilot suggests code completions as developers type and turns natural language prompts into
                    coding suggestions based on the project&apos;s context and style conventions.
                  </Text>
                </River.Content>
              </River>

              <River imageTextRatio="60:40">
                <River.Visual aria-hidden="true">
                  <a
                    href="https://docs.github.com/en/copilot/github-copilot-enterprise/copilot-docset-management/about-copilot-docset-management"
                    data-analytics-event={copilotDocsHydroTracking}
                    tabIndex={-1}
                    aria-hidden="true"
                  >
                    <AutoPlayVideo
                      src="/images/modules/site/copilot/features-river-2.mp4"
                      poster="/images/modules/site/copilot/features-river-2-poster.webp"
                      width="708"
                      height="472"
                      className="d-none d-md-block hide-reduced-motion"
                    />
                    <AutoPlayVideo
                      src="/images/modules/site/copilot/features-river-2-sm.mp4"
                      poster="/images/modules/site/copilot/features-river-2-poster-sm.webp"
                      width="350"
                      height="290"
                      className="d-block d-md-none hide-reduced-motion"
                    />
                    <Image
                      src="/images/modules/site/copilot/features-river-2.webp"
                      alt="GitHub Copilot showing different credential sources"
                      width="708"
                      height="472"
                      className="hide-no-pref-motion"
                      loading="lazy"
                    />
                  </a>
                </River.Visual>
                <River.Content>
                  <Heading as="h3">
                    <Label color="purple-red" className="lp-Label--inRiver label-purple-green">
                      Available for Copilot Enterprise
                    </Label>
                    Docs that feel tailored for you.
                  </Heading>
                  <Text as="p" variant="muted" className="lp-River-text">
                    Spend less time searching and more time learning, by getting personalized answers that are grounded
                    in your organization’s knowledge base, with inline citations. Load content → Ask question → Profit.
                  </Text>
                </River.Content>
              </River>

              <River imageTextRatio="60:40" style={{paddingBottom: '0'}}>
                <River.Visual aria-hidden="true">
                  <a
                    href="https://docs.github.com/en/copilot/github-copilot-enterprise/copilot-pull-request-summaries/about-copilot-pull-request-summaries"
                    data-analytics-event={copilotPullRequestsHydroTracking}
                    tabIndex={-1}
                    aria-hidden="true"
                  >
                    <AutoPlayVideo
                      src="/images/modules/site/copilot/features-river-3.mp4"
                      poster="/images/modules/site/copilot/features-river-3-poster.webp"
                      width="708"
                      height="472"
                      className="d-none d-md-block hide-reduced-motion"
                    />
                    <AutoPlayVideo
                      src="/images/modules/site/copilot/features-river-3-sm.mp4"
                      poster="/images/modules/site/copilot/features-river-3-poster-sm.webp"
                      width="350"
                      height="290"
                      className="d-block d-md-none hide-reduced-motion"
                    />
                    <Image
                      src="/images/modules/site/copilot/features-river-3.webp"
                      alt="GitHub Copilot adding a summary to a pull request"
                      width="708"
                      height="472"
                      className="hide-no-pref-motion"
                      loading="lazy"
                    />
                  </a>
                </River.Visual>
                <River.Content>
                  <Heading as="h3">
                    <Label color="purple-red" className="lp-Label--inRiver label-purple-green">
                      Available for Copilot Enterprise
                    </Label>
                    Pull requests that tell a story.
                  </Heading>
                  <Text as="p" variant="muted" className="lp-River-text">
                    GitHub Copilot keeps track of your work, suggests descriptions, and helps reviewers reason about
                    your changes.
                  </Text>
                </River.Content>
              </River>
            </Grid.Column>

            {/* Spacer */}
            <Box paddingBlockStart={{narrow: 96, regular: 128}} aria-hidden={true} />

            <Grid.Column span={12}>
              <Bento className="Bento Bento--raised">
                <Bento.Item
                  columnSpan={12}
                  rowSpan={{xsmall: 8, small: 6, medium: 5, large: 5}}
                  visualAsBackground
                  className="lp-Features-bento-5"
                >
                  <Bento.Content
                    padding={{xsmall: 'normal', xlarge: 'spacious'}}
                    className="lp-Features-bento-5-content"
                  >
                    <Bento.Heading as="h3" size="5" weight="semibold">
                      <Label className="lp-Label--inBento">Coming soon as an add-on</Label>
                      Prefer bespoke? Fine-tune a private, custom model that suggests code based on the best practices
                      and patterns in your repositories.
                    </Bento.Heading>
                  </Bento.Content>
                </Bento.Item>

                <Bento.Item
                  columnSpan={{xsmall: 12, medium: 7}}
                  rowSpan={{xsmall: 5, medium: 5, large: 5, xlarge: 6}}
                  visualAsBackground
                >
                  <Bento.Content padding={{xsmall: 'normal', xlarge: 'spacious'}} verticalAlign="start">
                    <Bento.Heading as="h3" size="4" weight="semibold">
                      Ask for assistance right in your terminal.
                    </Bento.Heading>
                    <Link
                      href="https://docs.github.com/copilot/github-copilot-in-the-cli"
                      size="large"
                      variant="default"
                    >
                      Try Copilot in the CLI
                    </Link>
                  </Bento.Content>
                  <Bento.Visual position="0% 50%">
                    <Image
                      src="/images/modules/site/copilot/features-bento-1.webp"
                      alt="Screenshot of GitHub Copilot CLI in a terminal"
                      width="724"
                      height="620"
                      className="lp-Features-bento-1-visual"
                      loading="lazy"
                    />
                  </Bento.Visual>
                </Bento.Item>

                <Bento.Item
                  columnSpan={{xsmall: 12, medium: 5}}
                  rowSpan={{xsmall: 5, medium: 5, large: 5, xlarge: 6}}
                  className="Bento-item"
                >
                  <Bento.Content padding={{xsmall: 'normal', xlarge: 'spacious'}} horizontalAlign="center">
                    <Bento.Heading as="h3" size="4" weight="semibold">
                      Keep flying with your favorite editor.
                    </Bento.Heading>
                  </Bento.Content>
                  <Bento.Visual
                    padding={{xsmall: 'normal', xlarge: 'spacious'}}
                    fillMedia={false}
                    className="lp-Features-editorContainer"
                  >
                    <Button
                      as="a"
                      href="https://marketplace.visualstudio.com/items?itemName=GitHub.copilot"
                      hasArrow={false}
                      className="lp-Features-editorButton"
                    >
                      <Image
                        src="/images/modules/site/copilot/features-bento-2-vscode.svg"
                        alt=""
                        width="90"
                        height="90"
                        loading="lazy"
                      />
                      <Text as="div" size="200" weight="normal">
                        VS Code
                      </Text>
                    </Button>

                    <Button
                      as="a"
                      href="https://docs.github.com/copilot/getting-started-with-github-copilot?tool=vimneovim"
                      hasArrow={false}
                      className="lp-Features-editorButton"
                    >
                      <Image
                        src="/images/modules/site/copilot/features-bento-2-neovim.svg"
                        alt=""
                        width="90"
                        height="90"
                        loading="lazy"
                      />
                      <Text as="div" size="200" weight="normal">
                        Neovim
                      </Text>
                    </Button>

                    <Button
                      as="a"
                      href="https://marketplace.visualstudio.com/items?itemName=GitHub.copilotvs"
                      hasArrow={false}
                      className="lp-Features-editorButton"
                    >
                      <Image
                        src="/images/modules/site/copilot/features-bento-2-visualstudio.svg"
                        alt=""
                        width="90"
                        height="90"
                        loading="lazy"
                      />
                      <Text as="div" size="200" weight="normal">
                        Visual Studio
                      </Text>
                    </Button>

                    <Button
                      as="a"
                      href="https://plugins.jetbrains.com/plugin/17718-github-copilot"
                      hasArrow={false}
                      className="lp-Features-editorButton"
                    >
                      <Image
                        src="/images/modules/site/copilot/features-bento-2-jetbrains.svg"
                        alt=""
                        width="90"
                        height="90"
                        loading="lazy"
                      />
                      <Text as="div" size="200" weight="normal">
                        JetBrains IDEs
                      </Text>
                    </Button>
                  </Bento.Visual>
                </Bento.Item>

                <Bento.Item
                  columnSpan={{xsmall: 12, medium: 5, large: 5, xlarge: 5}}
                  rowSpan={{xsmall: 4, small: 4, medium: 4, xlarge: 5}}
                  className="Bento-item"
                >
                  <Bento.Content
                    horizontalAlign={{xsmall: 'center', large: 'start'}}
                    padding={{xsmall: 'normal', xlarge: 'spacious'}}
                    leadingVisual={<DeviceMobileIcon />}
                    className="lp-Features-mobile"
                  >
                    <Bento.Heading as="h3" size="4" weight="semibold" className="lp-Features-mobileText">
                      Coming soon to <br className="break-whenNarrow" /> GitHub Mobile.
                    </Bento.Heading>
                  </Bento.Content>
                  <Bento.Visual
                    padding={{xsmall: 'normal', xlarge: 'spacious'}}
                    fillMedia={false}
                    horizontalAlign="center"
                    verticalAlign="end"
                    style={{columnGap: '24px', flexWrap: 'wrap'}}
                  >
                    <a href="https://play.google.com/store/apps/details?id=com.github.android">
                      <Image
                        src="/images/modules/site/copilot/features-bento-4-google.png"
                        alt="Google Play Store logo"
                        width="180"
                        height="53"
                        style={{display: 'block'}}
                        loading="lazy"
                      />
                    </a>
                    <a href="https://apps.apple.com/app/github/id1477376905?ls=1">
                      <Image
                        src="/images/modules/site/copilot/features-bento-4-apple.png"
                        alt="Apple App Store logo"
                        width="179"
                        height="53"
                        style={{display: 'block'}}
                        loading="lazy"
                      />
                    </a>
                  </Bento.Visual>
                </Bento.Item>

                <Bento.Item
                  columnSpan={{xsmall: 12, medium: 7, large: 7, xlarge: 7}}
                  rowSpan={{xsmall: 4, small: 3, medium: 4, xlarge: 5}}
                  visualAsBackground
                >
                  <Bento.Visual position="25% 0%">
                    <Image
                      src="/images/modules/site/copilot/features-bento-3.webp"
                      alt="A phone showing GitHub Copilot in GitHub Mobile"
                      width="724"
                      height="560"
                      loading="lazy"
                    />
                  </Bento.Visual>
                </Bento.Item>
              </Bento>
            </Grid.Column>
          </Grid>
        </section>

        <section id="pricing" className="lp-Section lp-Section--pricing" ref={sectionPricingRef}>
          {shouldShowPricingTable ? (
            <PricingCards
              copilotSignupPath={copilotSignupPath}
              copilotForBusinessSignupPath={copilotForBusinessSignupPath}
              copilotContactSalesPath={copilotContactSalesPath}
            />
          ) : (
            <PricingGrouped
              copilotSignupPath={copilotSignupPath}
              copilotForBusinessSignupPath={copilotForBusinessSignupPath}
              copilotContactSalesPath={copilotContactSalesPath}
            />
          )}
        </section>

        {shouldShowPricingTable ? (
          <section className="lp-Section pt-0 pb-0">
            <PricingTable
              copilotSignupPath={copilotSignupPath}
              copilotForBusinessSignupPath={copilotForBusinessSignupPath}
              copilotContactSalesPath={copilotContactSalesPath}
            />
          </section>
        ) : (
          ''
        )}

        <section id="resources" className="lp-Section lp-Section--compact">
          <Grid className="lp-Section-container--centerUntilMedium lp-Grid--noRowGap">
            <Grid.Column span={12}>
              <SectionIntro fullWidth className="lp-SectionIntro">
                <SectionIntro.Heading size="3">Get the most out of GitHub Copilot.</SectionIntro.Heading>
              </SectionIntro>

              <Stack
                direction={{narrow: 'vertical', regular: 'horizontal', wide: 'horizontal'}}
                gap="normal"
                padding="none"
              >
                <div className="lp-Resources-card">
                  <Card ctaText="Explore GitHub Expert Services" href="https://github.com/services/">
                    <Card.Icon icon={<PeopleIcon />} color="purple" hasBackground />
                    <Card.Heading>Hands-on consulting, guided workshops, and training.</Card.Heading>
                    <Card.Description>
                      Insights, best practices, and knowledge to help you adopt GitHub quickly and efficiently.
                    </Card.Description>
                  </Card>
                </div>

                <div className="lp-Resources-card">
                  <Card ctaText="Read customer stories" href="https://github.com/customer-stories">
                    <Card.Icon icon={<BookIcon />} color="purple" hasBackground />
                    <Card.Heading>Meet the companies who build with GitHub.</Card.Heading>
                    <Card.Description>
                      Leading organizations choose GitHub to plan, build, secure and ship software.
                    </Card.Description>
                  </Card>
                </div>

                <div className="lp-Resources-card">
                  <Card ctaText="Read blog" href="https://github.blog/">
                    <Card.Icon icon={<BookIcon />} color="purple" hasBackground />
                    <Card.Heading>Keep up with the latest on GitHub and trends in AI.</Card.Heading>
                    <Card.Description>
                      Check out the GitHub blog for tips, technical guides, best practices, and more.
                    </Card.Description>
                  </Card>
                </div>
              </Stack>
            </Grid.Column>
          </Grid>
        </section>

        {/* FAQGroup content is managed through Contentful: */}
        {isFeatureCopilotPage(page) && (
          <section id="faq" className="lp-Section lp-Section--level-1">
            <Grid>
              <Grid.Column span={12} className="lp-FAQs">
                <ContentfulFaqGroup component={page.fields.template.fields.faqGroup} />
              </Grid.Column>
            </Grid>
          </section>
        )}

        <section id="footnotes" className="lp-Section lp-Section--level-1" style={{paddingTop: '0'}}>
          <Grid className="lp-Grid--noRowGap">
            <Grid.Column span={12}>
              <OrderedList>
                <OrderedList.Item className="lp-Footnotes-item">Chat in GitHub Mobile is coming soon.</OrderedList.Item>
                <OrderedList.Item className="lp-Footnotes-item">
                  Currently in preview. Instructions to enable Copilot in the CLI in preview can be found{' '}
                  <a className="lp-Link--inline" href="https://docs.github.com/copilot/github-copilot-in-the-cli">
                    here
                  </a>
                  .
                </OrderedList.Item>
              </OrderedList>
            </Grid.Column>
          </Grid>
        </section>
      </ThemeProvider>
    </>
  )
}

try{ CopilotIndex.displayName ||= 'CopilotIndex' } catch {}
try{ AutoPlayVideo.displayName ||= 'AutoPlayVideo' } catch {}